import { createFileRoute } from '@tanstack/react-router';
import { XClose, Zap } from '@untitled-ui/icons-react';
import { useEffect, useState } from 'react';

import { Button } from '../../components/buttons/button';
import { IconButton } from '../../components/buttons/icon-button';
import { FeaturedIcon } from '../../components/featured-icon';
import { OrganizationInviteStatus } from '../../entity/api/organization-user';
import { addTrackCrumb, trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import { getQueryClient } from '../../sdk/request';
import {
  getStores,
  useAllOrganizationUsers,
  useManageOrganizationInvitation,
} from '../../services/storage';
import {
  fetchOrganizationsListImperative,
  useOrganizationById,
} from '../../services/storage/organizations-queries';
import { tGlobal, tNamespace } from '../../services/translation';
import { updateAuthStorageData } from '../../shared/user';

const t = tNamespace('app.invite');

function handleLogout() {
  getStores()
    .users.getState()
    .signOut()
    .catch(function (err) {
      captureException(
        err,
        {
          file: 'app/invite.tsx',
          method: 'handleLogout',
          infoMessage: 'unable to logout',
        },
        trackError
      );
    });
}

export const Route = createFileRoute('/app/invite')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/invite',
      level: 'info',
    });

    const queryClient = getQueryClient();
    return Promise.allSettled([fetchOrganizationsListImperative(queryClient)]).catch(
      function (err) {
        captureException(
          err,
          {
            file: 'invite.tsx',
            method: 'beforeLoad',
            infoMessage: 'unable to fetch organizations from api',
          },
          trackError
        );

        throw err;
      }
    );
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  // TanStack Query for all org users
  const { data: allOrgUsersData } = useAllOrganizationUsers();
  const orgUserInvite = allOrgUsersData?.find(function (orgUser) {
    return orgUser?.inviteStatus === OrganizationInviteStatus.Pending;
  });
  const { data: organization } = useOrganizationById(orgUserInvite?.organizationId);

  // TanStack Query mutation
  const manageInvitationMutation = useManageOrganizationInvitation();

  function acceptInvite(orgId: string) {
    if (orgId == null || orgId.length === 0) {
      return;
    }

    setAcceptLoading(true);

    manageInvitationMutation.mutate(
      { organizationId: orgId, status: OrganizationInviteStatus.Accepted },
      {
        onSuccess: function () {
          updateAuthStorageData(orgId)
            .then(function () {
              // force a refresh
              window.location.href = '/app';
            })
            .catch(function (err) {
              captureException(
                err,
                {
                  file: 'app/invite.tsx',
                  method: 'acceptInvite',
                  infoMessage: 'unable to update auth storage data',
                },
                trackError
              );
            });
        },
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/invite.tsx',
              method: 'acceptInvite',
              infoMessage: 'unable to accept organization invitation',
            },
            trackError
          );
        },
        onSettled: function () {
          setAcceptLoading(false);
        },
      }
    );
  }

  function rejectInvite(orgId: string) {
    if (orgId == null || orgId.length === 0) {
      return;
    }

    setRejectLoading(true);

    manageInvitationMutation.mutate(
      { organizationId: orgId, status: OrganizationInviteStatus.Rejected },
      {
        onSuccess: function () {
          // force log out
          handleLogout();
        },
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/invite.tsx',
              method: 'rejectInvite',
              infoMessage: 'unable to reject organization invitation',
            },
            trackError
          );
        },
        onSettled: function () {
          setRejectLoading(false);
        },
      }
    );
  }

  useEffect(
    function () {
      const id = setTimeout(function () {
        // we want to make sure we have an org invite, otherwise it is
        // pointless to be on this page
        if (orgUserInvite?.organizationId != null) {
          return;
        }

        // force a refresh
        window.location.href = '/app';
      }, 100);
      return () => clearTimeout(id);
    },
    [orgUserInvite?.organizationId]
  );

  return (
    <div className="page page--auth relative flex h-[100vh] w-full min-w-[1000px] items-center overflow-hidden bg-branding-accent-950">
      <img
        src="/demo-art.png"
        className="absolute top-full w-1/2 -translate-y-1/2 transition-all duration-700"
      />
      <div className="relative mx-auto flex w-[480px] flex-col items-center justify-center rounded-xl bg-white p-6">
        <IconButton
          icon={XClose}
          variant="outline-circular"
          tabIndex={-1}
          size="md"
          className="absolute right-2 top-2"
          onClick={() => rejectInvite(orgUserInvite?.organizationId ?? '')}
        />
        <FeaturedIcon icon={<Zap />} color="accent" border={false} size="md" />
        <p className="font-display-3 mt-6 text-center">
          {t('title', { orgName: organization?.name ?? '' })}
        </p>
        <p className="font-body-2-r mt-1 text-center text-gray">{t('joinAndCollaborate')}</p>
        <div className="mt-8 flex w-full gap-4">
          <Button
            variant="outline"
            size="xl"
            className="w-full"
            onClick={() => rejectInvite(orgUserInvite?.organizationId ?? '')}
            loading={rejectLoading}
          >
            {tGlobal('decline')}
          </Button>
          <Button
            variant="accent"
            size="xl"
            className="w-full"
            onClick={() => acceptInvite(orgUserInvite?.organizationId ?? '')}
            loading={acceptLoading}
          >
            {tGlobal('accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
